/**
 * Clients (Figma 09 · Clients 01–11): everyone outside the team who can open
 * this brand. A client is a Guest (link-based clients and share links were
 * removed on 1 Oct, so the design's "Share links" are not built). Owner +
 * Contributor read; only the Owner gives or removes access.
 * Not built, no backend yet: the client's Name, "Since / invited by",
 * Resend / Copy link, and Undo after removing access.
 */
import { useState } from "react";
import { Ellipsis, Hourglass, MessageSquare, Store, UserMinus } from "lucide-react";
import { queue } from "@/components/Toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGetBrands } from "@/hooks/useGetBrands";
import {
  useBrandGuests,
  useCancelBrandGuestInvite,
  useRemoveBrandGuest,
  type BrandGuest,
  type BrandGuestInvite,
} from "@/hooks/useBrandGuests";
import {
  useOrganizationMembers,
  useUpdateMemberRole,
} from "@/app/routes/dashboard/settings/api/useOrganization";
import { KitButton } from "../../kit/Button";
import { KitIconButton } from "../../kit/IconButton";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../../kit/Menu";
import { BrandMark } from "../../kit/BrandPicker";
import { EmptyState, ErrorState, StateAction } from "../../ViewStates";
import {
  ConfirmDialog,
  DetailCard,
  DetailHead,
  Fact,
  Facts,
  GroupLabel,
  IndexCard,
  IndexRow,
  PageHead,
  PersonMark,
  SectionHead,
  SettingsPage,
  Split,
  SplitLoading,
  StatusLabel,
} from "../parts/SettingsParts";
import { NeedBrand, useBrandHead } from "../parts/scope";
import { EditBrandsDialog, InviteDialog } from "../parts/dialogs";
import { formatDay, formatMoment, plural } from "../parts/settingsFormat";

type Selection = { kind: "guest"; id: string } | { kind: "invite"; id: string };

const firstName = (g: { name?: string | null; email: string }) => (g.name || g.email).split(/[\s@]/)[0];

export default function ClientsPage() {
  const head = useBrandHead();
  const brandId = head.brand?.id ?? null;
  const brandName = head.brandName;
  const { selectedOrganization } = useCurrentUser();
  const orgId = selectedOrganization?.organization.id ?? null;
  const isOwner = selectedOrganization?.role === "Owner";
  const guestsQuery = useBrandGuests(brandId);
  const { data: members } = useOrganizationMembers(orgId);
  const { data: brands } = useGetBrands();
  const updateRole = useUpdateMemberRole(orgId);
  const removeGuest = useRemoveBrandGuest(brandId);
  const cancelInvite = useCancelBrandGuestInvite(brandId);

  const [picked, setPicked] = useState<Selection | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<BrandGuest | null>(null);
  const [removeTarget, setRemoveTarget] = useState<BrandGuest | null>(null);

  const guests = guestsQuery.data?.guests ?? [];
  const invites = guestsQuery.data?.invites ?? [];
  const brandsOf = (userId: string) => members?.find((m) => m.userId === userId)?.brands ?? [];

  const selectedGuest = picked?.kind === "guest" ? guests.find((g) => g.userId === picked.id) : undefined;
  const selectedInvite = picked?.kind === "invite" ? invites.find((i) => i.id === picked.id) : undefined;
  const shownGuest = selectedGuest ?? (!selectedInvite ? guests[0] : undefined);
  const shownInvite = selectedInvite ?? (!shownGuest ? invites[0] : undefined);

  const status = !isOwner
    ? "Only owners give or remove access."
    : guests.length + invites.length === 0
      ? `Nobody outside the team can see ${brandName} yet.`
      : `Everyone outside the team who can see ${brandName}. Clients take no seat.`;

  return (
    <SettingsPage>
      <PageHead
        {...head}
        title="Clients"
        status={brandId ? status : undefined}
        action={isOwner && brandId ? <KitButton onClick={() => setAddOpen(true)}>Add a client</KitButton> : null}
      />
      {!brandId ? (
        <NeedBrand />
      ) : guestsQuery.isLoading ? (
        <SplitLoading caption={`Loading ${brandName}’s clients`} rows={3} />
      ) : guestsQuery.isError ? (
        <ErrorState
          title={`${brandName}’s clients did not load`}
          body={`Creo could not read who can see ${brandName}. Nobody’s access has changed.`}
          onRetry={() => void guestsQuery.refetch()}
        />
      ) : guests.length + invites.length === 0 ? (
        <EmptyState
          icon={<MessageSquare aria-hidden />}
          title="Nobody outside the team yet"
          body={`Give ${brandName}’s people a login to review the calendar and concepts. Clients take no seat and never see costs.`}
          actions={isOwner ? <StateAction primary onClick={() => setAddOpen(true)}>Add a client</StateAction> : null}
        />
      ) : (
        <Split
          backLabel="Clients"
          index={
            <IndexCard>
              <GroupLabel>Client logins</GroupLabel>
              {guests.map((g) => {
                const n = brandsOf(g.userId).length;
                return (
                  <IndexRow
                    key={g.userId}
                    lead={<PersonMark name={g.name || g.email} picture={g.profilePicture} />}
                    title={g.name || g.email}
                    sub={`Login${n > 1 ? ` · ${plural(n, "brand")}` : ""}`}
                    active={shownGuest?.userId === g.userId}
                    onPress={() => setPicked({ kind: "guest", id: g.userId })}
                  />
                );
              })}
              {invites.map((i) => (
                <IndexRow
                  key={i.id}
                  lead={
                    <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-full border border-co-line2 text-co-muted">
                      <Hourglass className="size-4" />
                    </span>
                  }
                  title={i.email}
                  sub={`Invitation sent · expires ${formatDay(i.expiresAt)}`}
                  active={shownInvite?.id === i.id}
                  onPress={() => setPicked({ kind: "invite", id: i.id })}
                />
              ))}
            </IndexCard>
          }
          detail={
            shownGuest ? (
              <GuestDetail
                guest={shownGuest}
                brandName={brandName}
                brands={brandsOf(shownGuest.userId)}
                isOwner={isOwner}
                onEdit={() => setEditTarget(shownGuest)}
                onRemove={() => setRemoveTarget(shownGuest)}
              />
            ) : shownInvite ? (
              <InviteDetail
                invite={shownInvite}
                brandName={brandName}
                isOwner={isOwner}
                inviter={members?.find((m) => m.userId === shownInvite.invitedBy)}
                onRevoke={() => {
                  cancelInvite.mutate(shownInvite.id);
                  setPicked(null);
                }}
              />
            ) : null
          }
        />
      )}

      {addOpen ? (
        <InviteDialog
          clientOnly
          organizationId={orgId}
          organizationName={selectedOrganization?.organization.name ?? ""}
          seatsLeft={null}
          maxSeats={null}
          initialBrandIds={brandId ? [brandId] : []}
          onClose={() => setAddOpen(false)}
        />
      ) : null}

      {editTarget ? (
        <EditBrandsDialog
          name={firstName(editTarget)}
          initial={brandsOf(editTarget.userId).map((b) => b.id)}
          onClose={() => setEditTarget(null)}
          onSave={(ids) => {
            const picked = (brands ?? []).filter((b) => ids.includes(b.id)).map(({ id, name }) => ({ id, name }));
            updateRole.mutate({ userId: editTarget.userId, role: "Guest", brands: picked });
          }}
        />
      ) : null}

      {removeTarget ? (
        <ConfirmDialog
          title={`Remove ${firstName(removeTarget)}’s access to ${brandName}?`}
          description={`${firstName(removeTarget)} stops seeing ${brandName} at once. Nothing they wrote is deleted.`}
          consequences={[
            { icon: Store, text: `${firstName(removeTarget)}’s login no longer opens ${brandName}. Other brands are not affected.` },
          ]}
          confirmLabel="Remove access"
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => {
            const target = removeTarget;
            removeGuest.mutate(target.userId, {
              onSuccess: () => queue.add({ title: `${firstName(target)} no longer sees ${brandName}`, variant: "success" }),
            });
            setPicked(null);
            setRemoveTarget(null);
          }}
        />
      ) : null}
    </SettingsPage>
  );
}

function GuestDetail({
  guest,
  brandName,
  brands,
  isOwner,
  onEdit,
  onRemove,
}: {
  guest: BrandGuest;
  brandName: string;
  brands: { id: string; name: string }[];
  isOwner: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const name = guest.name || guest.email;
  return (
    <DetailCard>
      <DetailHead
        lead={<PersonMark name={name} picture={guest.profilePicture} size={56} />}
        title={name}
        sub={guest.email}
        aside={
          isOwner ? (
            <>
              <KitButton variant="secondary" onClick={onEdit}>
                Edit brands
              </KitButton>
              <KitMenuTrigger>
                <KitPressable>
                  <KitIconButton icon={Ellipsis} aria-label={`More for ${name}`} />
                </KitPressable>
                <KitMenuPopover>
                  <KitMenu aria-label="More" onAction={(key) => key === "remove" && onRemove()}>
                    <KitMenuItem id="remove" icon={UserMinus} danger>
                      Remove access to {brandName}
                    </KitMenuItem>
                  </KitMenu>
                </KitMenuPopover>
              </KitMenuTrigger>
            </>
          ) : null
        }
      />
      <Facts>
        <Fact label="Can see">{brandName}’s calendar and concepts, to review and comment</Fact>
        <Fact label="Login">Since {formatDay(guest.grantedAt)}</Fact>
        <Fact label="Last opened">{guest.lastSeenAt ? formatMoment(guest.lastSeenAt) : "Not yet"}</Fact>
        <Fact label="Seat">Takes no seat</Fact>
      </Facts>
      <SectionHead title={`Brands ${(guest.name || guest.email).split(/[\s@]/)[0]} can open`} meta={String(brands.length)} />
      <div className="-mx-2 flex flex-col">
        {brands.map((b) => (
          <div key={b.id} className="flex items-center gap-3 rounded-row px-2 py-2">
            <BrandMark brand={b} size={32} />
            <span className="flex flex-col">
              <span className="text-[13.5px] font-semibold text-co-text">{b.name}</span>
              <span className="text-[12px] text-co-muted">Calendar and concepts, for review</span>
            </span>
          </div>
        ))}
      </div>
    </DetailCard>
  );
}

function InviteDetail({
  invite,
  brandName,
  isOwner,
  inviter,
  onRevoke,
}: {
  invite: BrandGuestInvite;
  brandName: string;
  isOwner: boolean;
  inviter?: { name: string; email: string };
  onRevoke: () => void;
}) {
  const who = invite.email.split("@")[0];
  return (
    <DetailCard>
      <DetailHead
        lead={<PersonMark name={invite.email} size={56} />}
        title={invite.email}
        sub="Client invitation"
        aside={
          <>
            <StatusLabel icon={Hourglass}>Waiting for {who}</StatusLabel>
            {isOwner ? (
              <KitButton variant="secondary" onClick={onRevoke}>
                Revoke
              </KitButton>
            ) : null}
          </>
        }
      />
      <Facts>
        <Fact label="Can see">{brandName}’s calendar and concepts, to review and comment</Fact>
        <Fact label="Sent by">
          {[inviter?.name || inviter?.email, formatMoment(invite.createdAt).toLowerCase()].filter(Boolean).join(", ")}
        </Fact>
        <Fact label="Expires">{formatDay(invite.expiresAt)}</Fact>
        <Fact label="Seat">Takes no seat</Fact>
      </Facts>
    </DetailCard>
  );
}
